/* Discovery scrub hero — canvas frame-scrubbing tied to scroll.
   Self-contained custom element; no globals, no theme JS dependencies. */
(() => {
  'use strict';

  if (customElements.get('discovery-scrub')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE = window.matchMedia('(max-width: 699px)');
  const EAGER_FRAMES = 10; // decoded before first paint; rest stream in behind

  class DiscoveryScrub extends HTMLElement {
    connectedCallback() {
      this.classList.remove('dsh--nojs');
      // Mobile: skip the tall sticky scrub entirely — it reads as a "frozen" page
      // on phones and buries the funnel. Render the static hero (beat 1 + CTA) and
      // let the funnel scroll normally. Desktop keeps the scrub.
      if (REDUCED.matches || MOBILE.matches) {
        this.classList.add('is-static');
        return;
      }

      this.canvas = this.querySelector('canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');

      this.frameCount = parseInt(this.dataset.frameCount, 10) || 60;
      const base = this.dataset.frameBase;
      const prefix = MOBILE.matches ? this.dataset.prefixMobile : this.dataset.prefixDesktop;
      this.urls = Array.from({ length: this.frameCount }, (_, i) =>
        `${base}${prefix}${String(i + 1).padStart(4, '0')}.webp`
      );

      this.bitmaps = new Array(this.frameCount).fill(null);
      this.currentFrame = -1;
      this.targetFrame = 0;
      this.beats = Array.from(this.querySelectorAll('[data-beat]')).map((el) => ({
        el,
        from: parseFloat(el.dataset.from),
        to: parseFloat(el.dataset.to)
      }));

      // iOS Safari fires resize on toolbar collapse mid-scroll; only re-size the
      // canvas when the *width* changes — resizing during scroll leaks on Safari.
      this.viewportWidth = window.innerWidth;
      this.sizeCanvas();
      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (window.innerWidth !== this.viewportWidth) {
            this.viewportWidth = window.innerWidth;
            this.sizeCanvas();
            this.currentFrame = -1; // force redraw at new size
          }
        }, 150);
      };
      window.addEventListener('resize', this.onResize);

      this.onScroll = () => this.updateProgress();
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.preload();
      this.updateProgress();
      this.rafId = requestAnimationFrame(() => this.tick());
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.rafId);
      this.bitmaps.forEach((b) => b && b.close && b.close());
      this.bitmaps = [];
    }

    sizeCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.canvas.style.width = w + 'px';
      this.canvas.style.height = h + 'px';
    }

    async fetchFrame(i) {
      if (this.bitmaps[i]) return;
      try {
        // explicit Accept: Shopify CDN transcodes to heavier JPEG for Accept: */*
        const res = await fetch(this.urls[i], { mode: 'cors', headers: { Accept: 'image/webp,image/*' } });
        if (!res.ok) return;
        const blob = await res.blob();
        // createImageBitmap decodes off the main thread — no jank during scroll
        this.bitmaps[i] = await createImageBitmap(blob);
      } catch (e) {
        /* missing frame: nearest-loaded fallback covers it */
      }
    }

    async preload() {
      await Promise.all(
        Array.from({ length: Math.min(EAGER_FRAMES, this.frameCount) }, (_, i) => this.fetchFrame(i))
      );
      // keep the poster if nothing decoded (bad frame URL, offline) — never fade to a blank canvas
      if (this.bitmaps.some(Boolean)) this.classList.add('is-ready');
      // stream remaining frames, a few at a time, without blocking
      for (let i = EAGER_FRAMES; i < this.frameCount; i += 4) {
        await Promise.all([0, 1, 2, 3].map((o) => (i + o < this.frameCount ? this.fetchFrame(i + o) : null)));
      }
    }

    updateProgress() {
      const rect = this.getBoundingClientRect();
      const scrubDistance = rect.height - window.innerHeight;
      const progress = scrubDistance > 0 ? Math.min(1, Math.max(0, -rect.top / scrubDistance)) : 0;
      this.targetFrame = Math.round(progress * (this.frameCount - 1));
      this.updateBeats(progress);
    }

    updateBeats(p) {
      const FADE = 0.06;
      for (const beat of this.beats) {
        let opacity = 0;
        if (p >= beat.from && p <= beat.to) {
          // a beat starting at 0 must be fully visible on load, not fade in
          const inRamp = beat.from <= 0 ? 1 : Math.min(1, (p - beat.from) / FADE);
          const outRamp = beat.to >= 0.99 ? 1 : Math.min(1, (beat.to - p) / FADE);
          opacity = Math.min(inRamp, outRamp);
        }
        beat.el.style.opacity = opacity.toFixed(3);
        beat.el.style.visibility = opacity > 0.01 ? 'visible' : 'hidden';
        beat.el.style.transform = `translateY(${((1 - opacity) * 14).toFixed(1)}px)`;
      }
    }

    nearestLoaded(target) {
      if (this.bitmaps[target]) return target;
      for (let d = 1; d < this.frameCount; d++) {
        if (this.bitmaps[target - d]) return target - d;
        if (this.bitmaps[target + d]) return target + d;
      }
      return -1;
    }

    tick() {
      const frame = this.nearestLoaded(this.targetFrame);
      if (frame !== -1 && frame !== this.currentFrame) {
        this.currentFrame = frame;
        this.draw(this.bitmaps[frame]);
      }
      this.rafId = requestAnimationFrame(() => this.tick());
    }

    draw(bitmap) {
      const cw = this.canvas.width;
      const ch = this.canvas.height;
      // cover-fit: fill the viewport, center the overflow
      const scale = Math.max(cw / bitmap.width, ch / bitmap.height);
      const dw = bitmap.width * scale;
      const dh = bitmap.height * scale;
      this.ctx.drawImage(bitmap, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    }
  }

  customElements.define('discovery-scrub', DiscoveryScrub);
})();
